import React, { useState, useCallback } from 'react';
import ConversationsList from './ConversationsList';
import { useConversation } from '../../hooks/useConversation';
import './ConversationSidebar.css';

/**
 * ConversationSidebar Component
 * 
 * Main sidebar for chat history feature.
 * 
 * Features:
 * - List of past conversations
 * - Infinite scroll pagination
 * - "New Chat" button
 * - Collapse/expand toggle
 * - Error handling
 * 
 * @param {Function} onNewChat - Callback when "New Chat" is clicked
 */
const ConversationSidebar = ({ onNewChat = () => {} }) => {
  const {
    conversations,
    activeConversationId,
    isLoadingConversations,
    error,
    conversationCursor,
    activateConversation,
    fetchMoreConversations,
    clearError
  } = useConversation();

  // Local state for sidebar visibility (default open on desktop, closed on mobile)
  const [isSidebarOpen, setIsSidebarOpen] = useState(
    typeof window !== 'undefined' && window.innerWidth > 768
  );

  /**
   * Handle conversation selection
   */
  const handleSelectConversation = useCallback(async (conversationId) => {
    await activateConversation(conversationId);
  }, [activateConversation]);

  /**
   * Handle "New Chat" button click
   */
  const handleNewChat = useCallback(() => {
    activateConversation(null); // Clear active conversation
    onNewChat();
  }, [activateConversation, onNewChat]);

  /**
   * Handle load more conversations
   */
  const handleLoadMore = useCallback(async () => {
    console.log('[ConversationSidebar] Loading more conversations');
    await fetchMoreConversations();
  }, [fetchMoreConversations]);

  /**
   * Toggle sidebar visibility (mobile)
   */
  const handleToggleSidebar = useCallback(() => {
    setIsSidebarOpen(prev => !prev);
  }, []);

  /**
   * Close error banner
   */
  const handleCloseError = useCallback(() => {
    clearError();
  }, [clearError]);

  return (
    <>
      {/* Mobile Hamburger Button */}
      <button
        className="sidebar-toggle-mobile"
        onClick={handleToggleSidebar}
        aria-label={isSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
        title="Toggle conversations"
      >
        ☰
      </button>

      {/* Sidebar Container */}
      <aside
        className={`conversation-sidebar ${!isSidebarOpen ? 'closed' : 'open'}`}
        aria-label="Chat history"
      >
        {/* Header */}
        <div className="sidebar-header">
          <h2 className="sidebar-title">Conversations</h2>
          <button
            className="toggle-button"
            onClick={handleToggleSidebar}
            aria-label={isSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            title="Toggle conversations sidebar"
          >
            {isSidebarOpen ? '←' : '→'}
          </button>
        </div>

        {/* New Chat Button */}
        <button
          className={`new-chat-button ${activeConversationId === null ? 'active' : ''}`}
          onClick={handleNewChat}
          aria-pressed={activeConversationId === null}
        >
          <span className="button-icon">+</span>
          <span className="button-text">New Chat</span>
        </button>

        {/* Error Banner */}
        {error && (
          <div className="error-banner" role="alert">
            <span className="error-text">{error.message}</span>
            <button
              className="error-close"
              onClick={handleCloseError}
              aria-label="Close error"
            >
              ✕
            </button>
          </div>
        )}

        {/* Conversations List */}
        <ConversationsList
          conversations={conversations}
          activeConversationId={activeConversationId}
          onSelectConversation={handleSelectConversation}
          onLoadMore={handleLoadMore}
          isLoading={isLoadingConversations}
          hasMore={!!conversationCursor}
          error={error}
        />

        {/* Footer */}
        <div className="sidebar-footer">
          <p className="footer-text">
            {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
          </p>
        </div>
      </aside>

      {/* Mobile Backdrop (close sidebar when clicked) */}
      {isSidebarOpen && (
        <div
          className="sidebar-backdrop"
          onClick={handleToggleSidebar}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default ConversationSidebar;
